import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { FaSpinner } from "../utils/icons";
import { authService } from "../services/auth";
import { Alert, AlertDescription, Input, Label, Button } from "@/design-system/components/react";
import AuthLayout from "./AuthLayout";

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Clear the post-success redirect timer on unmount so it can't fire (and
  // navigate) after the component is gone — also keeps the timer from leaking
  // across test cases.
  const redirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (redirectTimer.current) clearTimeout(redirectTimer.current);
    },
    [],
  );

  // No token in the URL — show an invalid-link state immediately.
  if (!token) {
    return (
      <AuthLayout eyebrow="Reset password" heading="Set a new password">
        <Alert variant="destructive" className="mb-5 p-3 bg-warn-tint">
          <AlertDescription>
            This reset link is invalid or has expired. Please request a new one.
          </AlertDescription>
        </Alert>
        <p className="text-center font-mono text-[9px] tracking-[0.05em] text-fg-faint">
          <Link to="/forgot-password" className="hover:text-fg-muted transition-colors">
            Request a new reset link
          </Link>
        </p>
      </AuthLayout>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    try {
      await authService.resetPassword(token, newPassword);
      setSuccess(true);
      // Redirect to login after ~2 seconds (mirrors ChangePassword).
      redirectTimer.current = setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err: any) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Failed to reset password. The link may have expired.";
      setError(detail);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout eyebrow="Reset password" heading="Set a new password">
      {error && (
        <Alert variant="destructive" className="mb-5 p-3 bg-warn-tint">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert variant="warning" className="mb-5 p-3">
          <AlertDescription>
            Password reset successfully! Redirecting to sign in...
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="newPassword" className="text-fg-muted">
            New Password
          </Label>
          <Input
            id="newPassword"
            type="password"
            name="newPassword"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="confirmPassword" className="text-fg-muted">
            Confirm Password
          </Label>
          <Input
            id="confirmPassword"
            type="password"
            name="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
        </div>

        <Button type="submit" disabled={isLoading || success} className="w-full">
          {isLoading ? (
            <>
              <FaSpinner className="animate-spin mr-2 h-4 w-4" />
              <span>Resetting password...</span>
            </>
          ) : (
            <span>Reset Password</span>
          )}
        </Button>
      </form>
    </AuthLayout>
  );
};

export default ResetPassword;
